import React, { useMemo } from 'react';
import { Image } from 'antd';
import type { ImageProps as AntImageProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import {
  getNestedValue,
  parseArrayNotation,
  parseNestedPath,
} from '../../base/store/storePathUtils';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { getCoreConfig } from '../../config';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

/** 将相对路径 src 转为完整访问地址（含 auth_token），data:/blob:/http 等保持原样 */
function toDisplaySrc(src: string | undefined): string | undefined {
  if (!src || typeof src !== 'string') return src;
  if (/^(data:|blob:|https?:\/\/)/i.test(src)) return src;
  const viewRes = getCoreConfig().viewRes;
  return viewRes ? viewRes(src) : src;
}

// SVG-based placeholder image (clean gray background with centered icon, no white space)
const DEFAULT_FALLBACK = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNmMGYwZjAiLz48cGF0aCBkPSJNNzAgNzBhMTIgMTIgMCAxIDEgMCAyNCAxMiAxMiAwIDAgMSAwLTI0em02MCAxMEw4NSAxMzBsLTI1LTIwLTIwIDE1djE1aDEyMHYtNDB6IiBmaWxsPSIjYmZiZmJmIi8+PC9zdmc+";

/** 与 andStore.getDataSet 读法一致，但只读 dataSets，便于 useAndStore selector 在字段变更时触发重渲染 */
function readDataSetFieldFromState(
  dataSets: Record<string, any>,
  name: string,
  field: string,
): any {
  if (!name) return undefined;
  const { baseName, index } = parseArrayNotation(name);
  const root = dataSets[baseName];

  if (index !== null) {
    const arr = root?.value;
    if (!Array.isArray(arr) || arr[index] === undefined) return undefined;
    const item = arr[index];
    if (!field) return item;
    return getNestedValue(item, parseNestedPath(field));
  }

  if (!root) return undefined;
  if (!field) return root;
  return getNestedValue(root, parseNestedPath(field));
}

export interface AndImageProps extends AntImageProps, BaseComponentProps, DataSetConfig {
  remoteUrl?: string;
  objectFit?: React.CSSProperties['objectFit'];
}

export const AndImage: React.FC<AndImageProps> = ({
  id, sessionId, develop, dataSet, dataSetField, remoteUrl, objectFit = 'cover',
  src: propSrc,
  ...restProps
}) => {
  const getProps = useAndStore((state) => state.getProps);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 必须读 state.dataSets，不能只订阅 getDataSet 引用，否则 setDataSet 后组件不重渲染
  const storeValue = useAndStore((state) =>
    dataSetKey && dataSetField
      ? readDataSetFieldFromState(state.dataSets, dataSetKey, dataSetField)
      : undefined,
  );

  // 从 store 获取动态 props
  const storeProps = useMemo(() => {
    if (sessionId && id) {
      return getProps(sessionId, id);
    }
    return null;
  }, [sessionId, id, getProps]);

  // 计算最终的 src（优先 store，其次 storeProps，最后 prop），并通过 viewRes 转为完整地址
  const computedSrc = useMemo(() => {
    let raw: string | undefined;
    if (storeValue !== undefined && storeValue !== null && storeValue !== '') {
      raw = String(storeValue);
    } else if (storeProps?.src !== undefined) {
      raw = storeProps.src;
    } else {
      raw = propSrc;
    }
    return toDisplaySrc(raw);
  }, [storeValue, storeProps?.src, propSrc]);

  // 合并 props
  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (key !== 'src' && storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  const imageDomProps = useMemo(() => {
    const mergedStyles = mergedProps.styles as
      | { root?: React.CSSProperties; image?: React.CSSProperties; mask?: React.CSSProperties }
      | undefined;
    return prepareAntdDomProps(
      {
        ...mergedProps,
        src: computedSrc || undefined,
        fallback: mergedProps.fallback || DEFAULT_FALLBACK,
        // Disable preview in designer mode to prevent GrapesJS selection interference
        preview: develop ? false : mergedProps.preview,
        styles: {
          root: { width: '100%', height: '100%', ...mergedStyles?.root },
          image: { width: '100%', height: '100%', objectFit, ...mergedStyles?.image },
          mask: mergedStyles?.mask,
        },
      } as Record<string, unknown>,
      ['token'],
    );
  }, [mergedProps, computedSrc, develop, objectFit]);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex' }}>
      <Image {...imageDomProps} />
    </div>
  );
};

export default AndImage;

